<template>
  <div class="faq-page">
    <van-nav-bar
      :fixed="true"
      z-index="9"
      :title="$t('FAQ')"
      :border="false"
      left-arrow
      @click-left="goBack"
    />

    <main class="faq-page__content">
      <section class="faq-hero">
        <div class="faq-hero__copy">
          <h1>{{ $t('faqPage.heroTitle') }}</h1>
          <p>{{ $t('faqPage.heroSubtitle') }}</p>
        </div>
        <img
          class="faq-hero__illustration"
          src="@/assets/img/home-alignment/faq-hero.svg"
          alt=""
          aria-hidden="true"
        >
      </section>

      <label class="faq-search" :class="{ 'faq-search--focused': searchFocused }">
        <van-icon name="search" aria-hidden="true" />
        <input
          :value="query"
          type="search"
          autocomplete="off"
          :placeholder="$t('faqPage.searchPlaceholder')"
          @input="onSearchInput($event.target.value)"
          @focus="searchFocused = true"
          @blur="searchFocused = false"
        >
      </label>

      <section v-if="filteredItems.length" class="faq-list" :aria-label="$t('FAQ')">
        <article
          v-for="(item, index) in filteredItems"
          :key="`${item.question}-${index}`"
          class="faq-item"
        >
          <button
            type="button"
            class="faq-item__question"
            :aria-expanded="expandedIndex === index ? 'true' : 'false'"
            @click="toggleItem(index)"
          >
            <span
              class="faq-item__question-icon"
              :class="{ 'faq-item__question-icon--active': expandedIndex === index }"
              aria-hidden="true"
            >
              <van-icon name="question-o" />
            </span>
            <span class="faq-item__question-text">{{ item.question }}</span>
            <van-icon
              class="faq-item__arrow"
              :class="{ 'faq-item__arrow--active': expandedIndex === index }"
              :name="expandedIndex === index ? 'arrow-up' : 'arrow-down'"
              aria-hidden="true"
            />
          </button>

          <transition name="answer-fade">
            <div v-if="expandedIndex === index" class="faq-item__answer">
              {{ item.answer }}
            </div>
          </transition>
        </article>
      </section>

      <section v-else class="faq-empty">
        <van-icon name="search" aria-hidden="true" />
        <p>{{ $t('faqPage.noResults') }}</p>
      </section>

      <section class="support-card">
        <div class="support-card__icon" aria-hidden="true">
          <img src="@/assets/img/home-alignment/whatsapp.svg" alt="">
        </div>
        <div class="support-card__copy">
          <h2>{{ $t('faqPage.contactTitle') }}</h2>
          <p>{{ $t('faqPage.contactDesc') }}</p>
        </div>
        <button type="button" class="support-card__button" @click="openCustomerSupport">
          {{ $t('faqPage.contactSupport') }}
        </button>
      </section>

      <section class="helpful-section">
        <p>{{ $t('faqPage.helpfulQuestion') }}</p>
        <div class="helpful-section__actions">
          <button type="button" @click="onHelpfulYes">
            {{ $t('faqPage.helpfulYes') }}
          </button>
          <button type="button" @click="openCustomerSupport">
            {{ $t('faqPage.helpfulNo') }}
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { Icon } from 'vant';
import { LANGUAGE } from '@/common/constants';
import faqData from '@/components/FAQ/faqData';
import routerBack from '@/mixins/routerBack';
import { buildCustomerSupportWhatsAppUrl } from '@/common/customerSupport';
import { copyText } from '@/utils/clipboard';

const CUSTOMER_SERVICE_PHONE = '+62 822-2777-6752';

export default {
  name: 'FaqPage',
  mixins: [routerBack],
  components: {
    [Icon.name]: Icon
  },
  data() {
    return {
      query: '',
      expandedIndex: 0,
      searchFocused: false
    };
  },
  computed: {
    localizedItems() {
      const locale = this.$i18n.locale;
      let source = faqData.enList;
      if (locale === LANGUAGE.id) source = faqData.idList;
      if (locale === LANGUAGE.ph) source = faqData.phList;

      return source.map(item => ({
        question: String(item?.title || '').trim(),
        answer: String(item?.content || '').trim()
      }));
    },
    filteredItems() {
      const keyword = this.query.trim().toLocaleLowerCase();
      if (!keyword) return this.localizedItems;

      return this.localizedItems.filter(item => {
        const question = item.question.trim().toLocaleLowerCase();
        const answer = item.answer.trim().toLocaleLowerCase();
        return question.includes(keyword) || answer.includes(keyword);
      });
    }
  },
  methods: {
    onSearchInput(value) {
      this.query = value;
      this.expandedIndex = 0;
    },
    toggleItem(index) {
      this.expandedIndex = this.expandedIndex === index ? -1 : index;
    },
    onHelpfulYes() {
      this.$toast(this.$t('faqPage.helpfulYesToast'));
    },
    async openCustomerSupport() {
      try {
        const openedWindow = window.open(buildCustomerSupportWhatsAppUrl(this.$store.state.user.channel), '_blank');
        if (openedWindow) {
          try {
            openedWindow.opener = null;
          } catch {
            // Some browsers prevent changing the opener on an external page.
          }
          return;
        }
      } catch {
        // Fall back to copying the support number below.
      }

      await this.copyCustomerServicePhone();
    },
    async copyCustomerServicePhone() {
      const success = await copyText(CUSTOMER_SERVICE_PHONE);
      this.$toast(this.$t(success ? 'customerServicePop.copySuccess' : 'customerServicePop.copyFailed'));
    }
  }
};
</script>

<style lang="less" scoped>
.faq-page {
  min-height: 100vh;
  padding-top: 88px;
  box-sizing: border-box;
  background: #f4f8ff;
  color: #070d3f;

  ::v-deep .van-nav-bar__title {
    color: #070d3f;
    font-weight: 500;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #070d3f;
  }
}

.faq-page__content {
  padding: 16px 16px calc(48px + env(safe-area-inset-bottom));
}

.faq-hero {
  min-height: 176px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 20px;
  background: #0b45ff;
  box-sizing: border-box;
}

.faq-hero__copy {
  min-width: 0;
  flex: 1;

  h1 {
    margin: 0;
    color: #ffffff;
    font-size: 32px;
    line-height: 48px;
    font-weight: 600;
  }

  p {
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 20px;
    line-height: 32px;
    font-weight: 400;
  }
}

.faq-hero__illustration {
  flex: 0 0 116px;
  width: 116px;
  height: 128px;
  object-fit: contain;
}

.faq-search {
  height: 96px;
  margin-top: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 2px solid #e5eaf3;
  border-radius: 24px;
  background: #ffffff;
  box-sizing: border-box;
  transition: border-color 0.16s ease;

  &--focused {
    border-color: #0b45ff;
  }

  > .van-icon {
    flex: 0 0 auto;
    color: #6b7280;
    font-size: 36px;
  }

  input {
    min-width: 0;
    flex: 1;
    padding: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #070d3f;
    font: inherit;
    font-size: 36px;
    line-height: 36px;
    font-weight: 500;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }

    &::-webkit-search-cancel-button {
      width: 32px;
      height: 32px;
    }
  }
}

.faq-list {
  margin-top: 16px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
}

.faq-item + .faq-item {
  border-top: 2px solid #e5eaf3;
}

.faq-item__question {
  width: 100%;
  min-height: 96px;
  padding: 16px;
  border: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  background: transparent;
  color: inherit;
  text-align: left;
  box-sizing: border-box;
}

.faq-item__question-icon {
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #eaf3ff;
  color: #0b45ff;
  font-size: 36px;

  &--active {
    background: #0b45ff;
    color: #ffffff;
  }
}

.faq-item__question-text {
  min-width: 0;
  flex: 1;
  color: #070d3f;
  font-size: 26px;
  line-height: 36px;
  font-weight: 600;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.faq-item__arrow {
  flex: 0 0 auto;
  color: #6b7280;
  font-size: 36px;

  &--active {
    color: #0b45ff;
  }
}

.faq-item__answer {
  padding: 0 16px 16px 96px;
  color: #6b7280;
  font-size: 24px;
  line-height: 32px;
  font-weight: 400;
  white-space: pre-line;
}

.answer-fade-enter-active,
.answer-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.answer-fade-enter,
.answer-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.faq-empty {
  min-height: 160px;
  margin-top: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 20px;
  color: #6b7280;
  box-sizing: border-box;

  .van-icon {
    font-size: 36px;
  }

  p {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 500;
  }
}

.support-card {
  margin-top: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 20px;
  background: #f8faff;
  box-sizing: border-box;
}

.support-card__icon {
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #ffffff;

  img {
    width: 48px;
    height: 48px;
    display: block;
  }
}

.support-card__copy {
  min-width: 0;
  flex: 1;

  h2 {
    margin: 0;
    color: #070d3f;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
  }

  p {
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 20px;
    line-height: 32px;
    font-weight: 400;
  }
}

.support-card__button {
  flex: 0 0 auto;
  max-width: 210px;
  min-height: 80px;
  padding: 0 24px;
  border: 2px solid #0b45ff;
  border-radius: 16px;
  background: #ffffff;
  color: #0b45ff;
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
}

.helpful-section {
  margin-top: 16px;
  text-align: center;

  > p {
    margin: 0;
    color: #6b7280;
    font-size: 24px;
    line-height: 32px;
    font-weight: 500;
  }
}

.helpful-section__actions {
  margin-top: 16px;
  display: flex;
  gap: 16px;

  button {
    min-width: 0;
    flex: 1;
    height: 92px;
    padding: 0 20px;
    overflow: hidden;
    border: 2px solid #0b45ff;
    border-radius: 16px;
    background: #ffffff;
    color: #0b45ff;
    font-size: 22px;
    line-height: 32px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

@media (max-width: 340px) {
  .faq-hero__copy h1 {
    font-size: 28px;
    line-height: 40px;
  }

  .faq-hero__illustration {
    flex-basis: 96px;
    width: 96px;
    height: 112px;
  }

  .support-card {
    flex-wrap: wrap;
  }

  .support-card__button {
    width: 100%;
    max-width: none;
  }
}
</style>
